import { useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import {
  Sparkles, ChevronDown, RefreshCw, Loader2, CheckCircle2, AlertTriangle, HelpCircle, Users,
} from "lucide-react";

// Scheda prodotto intelligente (Fase 2): sintesi + punti di forza + punti d'attenzione + FAQ,
// generati dall'AI SOLO sulle fonti ufficiali (CGA/DIP/DIP Agg.). Lazy-load all'apertura.
export function SchedaAI({ code, name, isAdmin }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [regen, setRegen] = useState(false);

  const fetchScheda = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/products/${code}/scheda-ai`);
      setData(data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && !data) fetchScheda();
  };

  const rigenera = async () => {
    setRegen(true);
    try {
      const { data } = await api.post(`/admin/products/${code}/scheda-ai/rigenera`);
      setData(data);
      toast.success("Scheda rigenerata");
    } catch (e) { toast.error(apiError(e)); }
    finally { setRegen(false); }
  };

  return (
    <div className="mt-5 rounded-lg border border-forest/15 bg-[#F6F9F6]" data-testid={`scheda-ai-${code}`}>
      <button onClick={toggle} data-testid={`scheda-ai-toggle-${code}`}
        className="flex w-full items-center justify-between gap-2 px-4 py-3 text-left">
        <span className="flex items-center gap-2 text-sm font-semibold text-forest">
          <Sparkles className="h-4 w-4 text-terracotta" /> Scheda intelligente (AI)
        </span>
        <ChevronDown className={`h-4 w-4 text-forest transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="border-t border-forest/10 px-4 py-4" data-testid={`scheda-ai-body-${code}`}>
          {loading ? (
            <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Generazione della scheda in corso…
            </div>
          ) : !data || data.disponibile === false ? (
            <p className="py-2 text-sm text-amber-600">
              Scheda non disponibile: i documenti di polizza non sono ancora stati indicizzati per questo prodotto.
            </p>
          ) : (
            <div className="space-y-4 text-sm">
              {data.a_chi && (
                <div>
                  <p className="mb-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-forest/70">
                    <Users className="h-3.5 w-3.5" /> A chi è rivolto
                  </p>
                  <p className="text-muted-foreground">{data.a_chi}</p>
                </div>
              )}

              {data.cosa_copre?.length > 0 && (
                <div>
                  <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-forest/70">Cosa copre</p>
                  <ul className="space-y-1.5">
                    {data.cosa_copre.map((c, i) => (
                      <li key={i} className="flex items-start gap-2 text-muted-foreground">
                        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-forest" /> <span>{c}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {data.punti_forza?.length > 0 && (
                <div>
                  <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-forest/70">Punti di forza</p>
                  <div className="flex flex-wrap gap-1.5">
                    {data.punti_forza.map((c, i) => (
                      <span key={i} className="rounded-full bg-sage px-3 py-1 text-xs text-forest">{c}</span>
                    ))}
                  </div>
                </div>
              )}

              {data.punti?.length > 0 && (
                <div>
                  <p className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-amber-700">
                    <AlertTriangle className="h-3.5 w-3.5" /> Punti d'attenzione
                  </p>
                  <ul className="space-y-1.5">
                    {data.punti.map((c, i) => (
                      <li key={i} className="flex items-start gap-2 text-muted-foreground">
                        <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" /> <span>{c}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {data.faq?.length > 0 && (
                <div>
                  <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-forest/70">
                    <HelpCircle className="h-3.5 w-3.5" /> Domande frequenti
                  </p>
                  <div className="space-y-2.5">
                    {data.faq.map((f, i) => (
                      <div key={i} data-testid={`scheda-faq-${code}-${i}`}>
                        <p className="font-medium text-forest">{f.d}</p>
                        <p className="text-muted-foreground">{f.r}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex flex-wrap items-center justify-between gap-2 border-t border-forest/10 pt-3">
                <p className="text-[11px] italic text-muted-foreground">
                  Contenuto generato con l'ausilio di sistemi di Intelligenza Artificiale sulle Condizioni di polizza ufficiali (CGA/DIP) e da verificare a cura dell'intermediario iscritto al RUI prima di indicazioni definitive al cliente. Non sostituisce le Condizioni di polizza, che prevalgono in ogni caso.
                </p>
                {isAdmin && (
                  <Button size="sm" variant="outline" onClick={rigenera} disabled={regen}
                    data-testid={`scheda-ai-rigenera-${code}`} className="h-7 gap-1.5 text-xs text-forest">
                    {regen ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Rigenera
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
